import {
  Box,
  Button,
  ChevronLeftIcon,
  DismissIcon,
  Modal,
  Typography,
} from '@iheartjane/reefer';
import { clsx } from 'clsx';
import type { ReactNode } from 'react';

import styles from './rateAndReviewModal.module.css';

export interface RateAndReviewModalProps {
  /**
   * A body for the current phase of the flow (form, stepper, submitted).
   * Bodies render their own `RateAndReviewModal.Body` and
   * `RateAndReviewModal.Footer`, so each variant owns its actions.
   */
  children: ReactNode;
  /** Applied to the desktop dialog only. */
  className?: string;
  /** Full-screen on mobile, centered dialog otherwise. */
  isMobile: boolean;
  /** Renders a back button left of the title when provided. */
  onBack?: () => void;
  onRequestClose: () => void;
  open: boolean;
  /** Rendered below the title, stays pinned while the body scrolls. */
  productHeader?: ReactNode;
  testId?: string;
  /** Defaults to "Rate and review". */
  title?: string;
}

interface SlotProps {
  children: ReactNode;
  testId?: string;
}

/** Scrolling content area between sticky header and footer. */
function RateAndReviewModalBody({
  children,
  testId = 'rate-and-review-modal-body',
}: SlotProps) {
  return <Modal.Content data-testid={testId}>{children}</Modal.Content>;
}

/** Sticky footer - action bar */
function RateAndReviewModalFooter({
  children,
  testId = 'rate-and-review-modal-footer',
}: SlotProps) {
  return (
    <Modal.Footer className={styles['footer']} data-testid={testId}>
      {children}
    </Modal.Footer>
  );
}

/**
 * Modal shell for the Rate & Review flow.
 * Owns the frame (title, close, product header); the body passed as children
 * owns its content and actions.
 */
export function RateAndReviewModal({
  children,
  className,
  isMobile,
  onBack,
  onRequestClose,
  open,
  productHeader,
  testId = 'rate-and-review-modal',
  title = 'Rate and review',
}: RateAndReviewModalProps) {
  return (
    <Modal
      background="grays-white"
      className={isMobile ? undefined : clsx(styles['dialog'], className)}
      contentLabel={title}
      data-testid={testId}
      onRequestClose={onRequestClose}
      open={open}
      variant={isMobile ? 'full-screen' : 'standard-dialogue'}
    >
      <div className={styles['header']} data-testid={`${testId}-header`}>
        <div className={styles['titleRow']}>
          {onBack && (
            <Button.Icon
              className={styles['back']}
              icon={<ChevronLeftIcon />}
              label="Back"
              onClick={onBack}
            />
          )}
          <Typography
            as="h1"
            branded
            className={styles['title']}
            textAlign="center"
            variant="h2-semibold"
          >
            {title}
          </Typography>
          <Button.Icon
            className={styles['close']}
            icon={<DismissIcon />}
            label="Close"
            onClick={onRequestClose}
          />
        </div>
        {productHeader && (
          <Box pt={16} width="100%">
            {productHeader}
          </Box>
        )}
      </div>
      {children}
    </Modal>
  );
}

RateAndReviewModal.Body = RateAndReviewModalBody;
RateAndReviewModal.Footer = RateAndReviewModalFooter;
